import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import AgentAvatar from '../AgentAvatar';
import useGhostCursorStore from './useGhostCursorStore';
import useCanvasStore from './useCanvasStore';
import useAgentStore from '../useAgentStore';
import useGhostCursorFloat from './useGhostCursorFloat';
import {
  GHOST_CURSOR_ACTING_MS,
  GHOST_CURSOR_ENTRY_MS,
  WORKFLOW_OPERATOR_AGENT_NAME,
  getGhostCursorNameTagStyle,
  getWorkflowOperatorAvatarColor,
  isGhostCursorChatInProgress,
} from './ghostCursorUtils';

const RESUME_IDLE_MS = GHOST_CURSOR_ACTING_MS;
const ENTRY_GLIDE_MS = GHOST_CURSOR_ENTRY_MS;
const EXIT_GLIDE_MS = 900;
const SPRITE_SIZE = 48;
const CURSOR_OFFSET_X = -(SPRITE_SIZE / 2);
const CURSOR_OFFSET_Y = -(SPRITE_SIZE - 4);

const offscreenPos = rect => ({ x: rect.width + 120, y: rect.height * 0.5 });
const waitingPos = rect => ({ x: Math.max(40, rect.width - 180), y: 70 });

const NAME = WORKFLOW_OPERATOR_AGENT_NAME;
const AVATAR_COLOR = getWorkflowOperatorAvatarColor(NAME);

function moveToWaitingPos(wrapperRef, setOuterPos) {
  if (!wrapperRef?.current) return;
  setOuterPos(waitingPos(wrapperRef.current.getBoundingClientRect()));
}

export default function GhostCursorOverlay({ aiChatOpen, reactFlowInstance, wrapperRef }) {
  const theme = useTheme();
  const nameTagStyle = useMemo(
    () => getGhostCursorNameTagStyle(AVATAR_COLOR, theme),
    [theme]
  );
  const targetComponentId = useGhostCursorStore(s => s.targetComponentId);
  const actionLabel = useGhostCursorStore(s => s.actionLabel);
  const lastUpdateAt = useGhostCursorStore(s => s.lastUpdateAt);
  const chatStatus = useAgentStore(s => s.chatStatus);
  const chatInProgress = isGhostCursorChatInProgress(chatStatus);

  const [mode, setMode] = useState('hidden');
  const [outerPos, setOuterPos] = useState(null);
  const [instantJump, setInstantJump] = useState(false);
  const innerRef = useGhostCursorFloat(SPRITE_SIZE);
  const shownRef = useRef(false);
  const wrapperRectRef = useRef(null);

  const showThinkingLabel = mode === 'rest' && chatInProgress;

  useEffect(() => {
    const el = wrapperRef?.current;
    if (!el) return undefined;
    const update = () => {
      if (wrapperRef.current) {
        wrapperRectRef.current = wrapperRef.current.getBoundingClientRect();
      }
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('scroll', update, true);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update, true);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (!wrapperRef?.current) return undefined;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return undefined;

    const timers = [];
    let raf;

    if (aiChatOpen) {
      shownRef.current = true;
      setInstantJump(true);
      setOuterPos(offscreenPos(rect));
      setMode('entering');
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          setInstantJump(false);
          setOuterPos(waitingPos(rect));
        });
      });
      timers.push(
        setTimeout(() => {
          setMode('rest');
        }, ENTRY_GLIDE_MS)
      );
    } else if (shownRef.current) {
      setMode('exiting');
      setOuterPos(offscreenPos(rect));
      timers.push(
        setTimeout(() => {
          shownRef.current = false;
          setOuterPos(null);
          setMode('hidden');
        }, EXIT_GLIDE_MS)
      );
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [aiChatOpen, wrapperRef]);

  useEffect(() => {
    if (!shownRef.current || !aiChatOpen || chatInProgress) return undefined;
    setMode(prev => {
      if (prev !== 'acting') return prev;
      moveToWaitingPos(wrapperRef, setOuterPos);
      return 'rest';
    });
  }, [chatInProgress, aiChatOpen, wrapperRef]);

  useEffect(() => {
    if (!lastUpdateAt || !shownRef.current) return undefined;
    setMode('acting');
    const t = setTimeout(() => {
      moveToWaitingPos(wrapperRef, setOuterPos);
      setMode('rest');
    }, RESUME_IDLE_MS);
    return () => clearTimeout(t);
  }, [lastUpdateAt, wrapperRef]);

  useEffect(() => {
    if (mode !== 'acting' || !targetComponentId || !reactFlowInstance || !wrapperRef?.current) {
      return undefined;
    }
    let raf;
    const setPosWithThreshold = next => {
      setOuterPos(prev => {
        if (prev && Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5) {
          return prev;
        }
        return next;
      });
    };
    const tick = () => {
      const nodes = useCanvasStore.getState().nodes;
      const node = nodes.find(n => n.id === targetComponentId);
      const toScreen = reactFlowInstance.flowToScreenPosition;
      const rect = wrapperRectRef.current || wrapperRef.current?.getBoundingClientRect();
      if (!rect) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (!node || typeof toScreen !== 'function') {
        setPosWithThreshold(waitingPos(rect));
        raf = requestAnimationFrame(tick);
        return;
      }
      const width = node.width || 220;
      const height = node.height || 60;
      const client = toScreen({
        x: node.position.x + width / 2,
        y: node.position.y + height / 2,
      });
      setPosWithThreshold({ x: client.x - rect.left, y: client.y - rect.top });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, targetComponentId, reactFlowInstance, wrapperRef]);

  if (!outerPos) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'visible',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: `translate(${outerPos.x + CURSOR_OFFSET_X}px, ${outerPos.y + CURSOR_OFFSET_Y}px)`,
          transition: instantJump
            ? 'none'
            : 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
        }}
      >
        <div ref={innerRef} style={{ willChange: 'transform', position: 'relative' }}>
          <AgentAvatar
            agentName={NAME}
            name={NAME}
            size={SPRITE_SIZE}
            forceSprite
            animated={false}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: SPRITE_SIZE - 2,
              transform: 'translateX(-50%)',
              background: nameTagStyle.background,
              color: nameTagStyle.color,
              border: nameTagStyle.border,
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              boxShadow: nameTagStyle.boxShadow,
              letterSpacing: 0.1,
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            {NAME}
            {mode === 'acting' && actionLabel ? (
              <span style={{ color: nameTagStyle.statusColor, fontWeight: 500, marginLeft: 6 }}>
                · {actionLabel}
              </span>
            ) : null}
            {showThinkingLabel ? (
              <span style={{ color: nameTagStyle.statusColor, fontWeight: 500, marginLeft: 6 }}>
                · Thinking…
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
